import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, effect, inject, model, OnChanges, OnInit, SimpleChanges, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { xDecimalPlacesAsFractionDigits } from '@phoenix/common';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MentionDirective } from '../../../@core/directives/mention.directive';
import { TrimLeadingPipe, TrimPipe } from '../../../@core/pipes';
import { PhxInputAutoCompleteComponent } from '../../../shared/components/phx-input-auto-complete/phx-input-auto-complete.component';
import { PhxInputClipboardComponent } from '../../../shared/components/phx-input-clipboard/phx-input-clipboard.component';
import { PhxInputNumberComponent } from '../../../shared/components/phx-input-number/phx-input-number.component';
import { PhxInputPasswordComponent } from '../../../shared/components/phx-input-password/phx-input-password.component';
import { LocaleDatePipe } from '../../../shared/pipes/locale-date.pipe';

@Component({
  selector: '',
  templateUrl: './formly-phx-input.component.html',
  styleUrl: './formly-phx-input.component.scss',
  providers: [TrimLeadingPipe, TrimPipe, LocaleDatePipe],
  standalone: true,
  imports: [
    CommonModule,
    InputTextModule,
    InputNumberModule,
    ReactiveFormsModule,
    FormlyModule,
    PhxInputNumberComponent,
    PhxInputPasswordComponent,
    PhxInputAutoCompleteComponent,
    PhxInputClipboardComponent,
    MentionDirective
  ]
})
/**
 * use this component directly, only for ngx-formly
 */
export class FormlyPhxInputComponent extends FieldType implements AfterViewInit, OnChanges, OnInit {

  trimLeadingPipe = inject(TrimLeadingPipe);
  trimPipe = inject(TrimPipe);
  private readonly destroyRef = inject(DestroyRef);

  //variables for number input
  minFractionDigits = model<number>();
  maxFractionDigits = model<number>();

  constructor() {
    super();

    effect(() => {
      let minFractionDigits = this.minFractionDigits();
      let maxFractionDigits = this.maxFractionDigits();
      untracked(() => {
        if (maxFractionDigits < minFractionDigits) {
          this.maxFractionDigits.set(minFractionDigits);
        }
      })
    })
  }

  ngOnInit() {
    if (this.props?.trim || this.props?.trimLeading) {
      // valueChanges must be torn down; re-opening trimmed fields stacked listeners otherwise.
      this.formControl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(value => {
          let transformedValue;
          if (this.props?.trim)
            transformedValue = this.trimPipe.transform(value);
          if (this.props?.trimLeading)
            transformedValue = this.trimLeadingPipe.transform(value);
          if (transformedValue !== value) {
            this.formControl.setValue(transformedValue, { emitEvent: false });
          }
        });
    }

    if (this.props?.minFractionDigits != null)
      this.minFractionDigits.set(this.props.minFractionDigits);
    else if (this.model?.decimalPlaces)
      this.minFractionDigits.set(xDecimalPlacesAsFractionDigits(this.model.decimalPlaces));
    else
      this.minFractionDigits.set(2);

    if (this.props?.maxFractionDigits != null)
      this.maxFractionDigits.set(this.props.maxFractionDigits);
    else if (this.model?.decimalPlaces)
      this.maxFractionDigits.set(xDecimalPlacesAsFractionDigits(this.model.decimalPlaces));
    else
      this.maxFractionDigits.set(2);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.props?.currentValue?.disabled !== changes.props?.previousValue?.disabled) {
      this.configureFormControl();
    }
  }

  ngAfterViewInit() {
    this.configureFormControl();
  }

  // DEV-755 It looks like you're using the disabled attribute with a reactive form directive
  private configureFormControl() {
    if (this.props?.disabled) {
      this.formControl.disable({ emitEvent: false }); // Disable the control
    } else {
      this.formControl.enable({ emitEvent: false }); // Enable the control
    }
  }
}
